import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import type { Block, DateBlock } from '@basehub/data/replicache/index'
import * as PropertiesPanel from '../../properties-panel'
import * as React from 'react'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'

export const DateBlockPropertiesPanel = ({
  block,
  parent,
  path,
}: {
  block: DateBlock
  parent: Block
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Constraints" spacing="tight">
        <PropertiesPanel.BooleanOption
          name="Is required"
          value={block.constraints.isRequired}
          id={`el-${sourceBlockId}-pp-is-required`}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: {
                constraints: { isRequired: v },
              },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-is-required`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        <PropertiesPanel.BooleanOption
          name="Include time"
          id={`el-${sourceBlockId}-pp-include-time`}
          value={block.typeOptions.useTime}
          mutate={async (v) => {
            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              data: { type: block.type, typeOptions: { useTime: v } },
              path,
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache?.mutate.updateBlock(params)
            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-include-time`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-include-time`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
        {block.typeOptions.useTime && (
          // add note regarding timezone
          <em className="text-xs text-dark-muted">
            Time is displayed in your local timezone (
            {Intl.DateTimeFormat().resolvedOptions().timeZone}).
          </em>
        )}
      </PropertiesPanel.Group>

      <PropertiesPanel.CommonSearchConfigGroup
        block={block}
        parent={parent}
        path={path}
      />
    </PropertiesPanel.PropertiesContainer>
  )
}
